import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2 } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useWorkAreas } from '../../contexts/WorkAreaContext';
import { tasksApi, STATUS_LIST, STATUS_COLORS, PRIORITY_LABELS, PRIORITY_COLORS, matchesFilter } from './tasksApi';
import { TaskFilterBar, emptyFilter } from './TaskFilterBar';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

export const KanbanView = ({ onOpenTask }) => {
  const { workAreaNames, workAreaColors } = useWorkAreas();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(emptyFilter());
  const [columnsBy, setColumnsBy] = useState('task_status'); // task_status | task_priority | work_area_id

  const load = async () => {
    setLoading(true);
    try {
      const data = await tasksApi.listTasks();
      setTasks(data);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => tasks.filter((t) => matchesFilter(t, filter)), [tasks, filter]);

  const columns = useMemo(() => {
    if (columnsBy === 'task_status') {
      return STATUS_LIST.map((s) => ({
        id: s, label: s, items: filtered.filter((t) => (t.task_status || 'To Do') === s)
      }));
    }
    if (columnsBy === 'task_priority') {
      return [0, 1, 2, 3, 4, 5].map((p) => ({
        id: String(p), label: PRIORITY_LABELS[p],
        items: filtered.filter((t) => (t.task_priority ?? 3) === p)
      }));
    }
    // work_area_id
    return Object.entries(workAreaNames).map(([id, name]) => ({
      id, label: name, items: filtered.filter((t) => t.work_area_id === id)
    })).filter((c) => c.items.length > 0);
  }, [filtered, columnsBy, workAreaNames]);

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const taskId = result.draggableId;
    const newColumnId = result.destination.droppableId;
    const task = tasks.find((t) => t.task_id === taskId || t.id === taskId);
    if (!task) return;

    let patch = null;
    if (columnsBy === 'task_status' && task.task_status !== newColumnId) {
      patch = { task_status: newColumnId };
    } else if (columnsBy === 'task_priority' && String(task.task_priority) !== newColumnId) {
      patch = { task_priority: parseInt(newColumnId, 10) };
    } else if (columnsBy === 'work_area_id' && task.work_area_id !== newColumnId) {
      patch = { work_area_id: newColumnId };
    }
    if (!patch) return;

    // Optimistic update
    setTasks((prev) => prev.map((t) => (t.task_id === taskId ? { ...t, ...patch } : t)));
    try {
      await tasksApi.updateTask(taskId, patch);
      toast.success('Task updated');
    } catch (e) {
      toast.error(e.message);
      load();
    }
  };

  return (
    <div data-testid="kanban-view" className="space-y-2">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-semibold text-muted-foreground">Group columns by</span>
        {[
          { id: 'task_status', label: 'Status' },
          { id: 'task_priority', label: 'Priority' },
          { id: 'work_area_id', label: 'Work area' },
        ].map((opt) => (
          <Button key={opt.id} size="sm" variant={columnsBy === opt.id ? 'default' : 'outline'}
            className="h-7 text-xs" onClick={() => setColumnsBy(opt.id)}
            data-testid={`kanban-cols-${opt.id}`}>
            {opt.label}
          </Button>
        ))}
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} tasks</span>
      </div>

      <TaskFilterBar value={filter} onChange={setFilter} />

      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground text-xs">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> loading
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex gap-3 overflow-x-auto pb-2" style={{ maxHeight: 'calc(100vh - 320px)' }}>
            {columns.map((col) => (
              <Droppable key={col.id} droppableId={String(col.id)}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={cn(
                      'w-72 flex-shrink-0 rounded-md border-2 p-2 transition-colors',
                      STATUS_COLORS[col.id] || 'bg-card border-border',
                      snapshot.isDraggingOver && 'border-violet-500 ring-2 ring-violet-500/20'
                    )}
                    data-testid={`kanban-col-${col.id}`}
                  >
                    <div className="flex items-center justify-between px-1 pb-2 mb-2 border-b border-border/50">
                      <h3 className="text-xs font-semibold">{col.label}</h3>
                      <Badge variant="outline" className="text-[10px] h-4">{col.items.length}</Badge>
                    </div>
                    <div className="space-y-1.5 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
                      {col.items.map((task, idx) => (
                        <Draggable key={task.task_id} draggableId={task.task_id} index={idx}>
                          {(prov, snap) => (
                            <div
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              {...prov.dragHandleProps}
                              onClick={() => onOpenTask && onOpenTask(task)}
                              className={cn(
                                'bg-card border border-border rounded p-2 cursor-pointer hover:border-violet-500/50 transition-colors',
                                snap.isDragging && 'shadow-lg ring-2 ring-violet-500/30'
                              )}
                              data-testid={`kanban-card-${task.task_id}`}
                            >
                              <div className="flex items-center gap-1 mb-1">
                                <Badge className={cn('text-[9px] h-4 px-1', PRIORITY_COLORS[task.task_priority ?? 3])}>
                                  {PRIORITY_LABELS[task.task_priority ?? 3]}
                                </Badge>
                                {task.work_area_id && workAreaNames[task.work_area_id] && (
                                  <Badge
                                    variant="outline"
                                    className="text-[9px] h-4 px-1 truncate max-w-[110px]"
                                    style={{ backgroundColor: workAreaColors[task.work_area_id], color: '#fff' }}
                                  >
                                    {workAreaNames[task.work_area_id]}
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs font-medium line-clamp-2">{task.task_title}</div>
                              {task.task_local_date && (
                                <div className="text-[10px] text-muted-foreground mt-1">
                                  📅 {new Date(task.task_local_date).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  </div>
                )}
              </Droppable>
            ))}
          </div>
        </DragDropContext>
      )}
    </div>
  );
};

export default KanbanView;
